import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  adminListProjects,
  adminResetProject,
  adminTransferProjectOwner,
  type AdminProjectListItem,
} from '../../api/adminProjects';
import Breadcrumb from '../../components/Breadcrumb';
import UserSearchInput from '../../components/UserSearchInput';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';
import TransferOwnerModal from './TransferOwnerModal';
import ResetProjectModal from './ResetProjectModal';

const PAGE_SIZE = 20;

export default function AdminProjectManager() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [q, setQ] = useState('');
  const [committedQ, setCommittedQ] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  // Bumped on "clear filters" to remount UserSearchInput (it owns its own text).
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [page, setPage] = useState(1);
  const [list, setList] = useState<AdminProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [transferTarget, setTransferTarget] = useState<AdminProjectListItem | null>(null);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [transferring, setTransferring] = useState(false);

  const [resetTarget, setResetTarget] = useState<AdminProjectListItem | null>(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  function getErrorMessage(e: unknown): string {
    if (
      e
      && typeof e === 'object'
      && 'code' in e
      && e.code === 'director_mapping_settlement_pending'
    ) {
      return t('projects.directorMappingSettlementPending');
    }
    return e instanceof Error ? e.message : t('projects.operationFailed');
  }

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminListProjects({
          page,
          limit: PAGE_SIZE,
          ...(committedQ ? { q: committedQ } : {}),
          ...(ownerFilter ? { ownerId: ownerFilter } : {}),
        });
        if (!ignore) {
          setList(res.data);
          setTotal(res.meta.total);
        }
      } catch (e) {
        if (!ignore) toast.error(getErrorMessage(e));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedQ, ownerFilter, page, toast, t]);

  const openTransferModal = (project: AdminProjectListItem): void => {
    setTransferTarget(project);
    setNewOwnerId('');
  };

  const closeTransferModal = (): void => {
    setTransferTarget(null);
    setNewOwnerId('');
  };

  const confirmTransfer = async (): Promise<void> => {
    if (!transferTarget || !newOwnerId) return;
    if (newOwnerId === transferTarget.ownerId) {
      toast.error(t('projects.targetAlreadyOwner'));
      return;
    }
    setTransferring(true);
    try {
      await adminTransferProjectOwner(transferTarget.id, newOwnerId);
      toast.success(t('projects.transferred'));
      closeTransferModal();
      const res = await adminListProjects({
        page,
        limit: PAGE_SIZE,
        ...(committedQ ? { q: committedQ } : {}),
        ...(ownerFilter ? { ownerId: ownerFilter } : {}),
      });
      setList(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setTransferring(false);
    }
  };

  const openResetModal = (project: AdminProjectListItem): void => {
    setResetTarget(project);
    setResetConfirmText('');
  };

  const closeResetModal = (): void => {
    setResetTarget(null);
    setResetConfirmText('');
  };

  const confirmReset = async (): Promise<void> => {
    if (!resetTarget) return;
    if (resetConfirmText !== resetTarget.name) return;
    setResetting(true);
    try {
      await adminResetProject(resetTarget.id, resetTarget.name);
      toast.success(t('projects.projectReset'));
      closeResetModal();
      const res = await adminListProjects({
        page,
        limit: PAGE_SIZE,
        ...(committedQ ? { q: committedQ } : {}),
        ...(ownerFilter ? { ownerId: ownerFilter } : {}),
      });
      setList(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setResetting(false);
    }
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      setCommittedQ(q);
      if (page !== 1) setPage(1);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.65rem 0.5rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'var(--text-3)',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface-2)',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.65rem 0.5rem',
    fontSize: '0.88rem',
    borderBottom: '1px solid var(--border)',
  };

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('projects.breadcrumbHome'), to: '/projects' }, { label: t('projects.title') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('projects.title')}</h1>
      </div>

      <div className="card admin-tickets-filters" style={{ padding: '0.9rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            aria-label={t('projects.searchProject')}
            className="input"
            placeholder={t('projects.searchProject')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKey}
            style={{ minWidth: 220, flex: '0 1 260px' }}
          />
          <UserSearchInput
            key={filterResetKey}
            ariaLabel={t('projects.searchOwner')}
            placeholder={t('projects.searchOwner')}
            onSelect={(user) => {
              const id = user?.id ?? '';
              if (ownerFilter !== id) {
                setOwnerFilter(id);
                if (page !== 1) setPage(1);
              }
            }}
            style={{ minWidth: 180, flex: '0 1 220px' }}
          />
          {(committedQ || ownerFilter) && (
            <button
              className="btn btn-ghost"
              style={{ fontSize: '0.8rem' }}
              onClick={() => {
                setQ('');
                setCommittedQ('');
                setOwnerFilter('');
                setFilterResetKey((k) => k + 1);
                if (page !== 1) setPage(1);
              }}
            >
              {t('projects.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('loading', { ns: 'common' })}</div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('projects.noProjects')}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('projects.projectName')}</th>
                <th style={thStyle}>{t('projects.currentOwner')}</th>
                <th style={thStyle}>{t('projects.createdAt')}</th>
                <th style={{ ...thStyle, textAlign: 'right', paddingRight: '1rem' }}>{t('projects.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    <Link
                      to={`/projects/${p.id}/script-summary`}
                      style={{ color: 'var(--primary)', textDecoration: 'none' }}
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-2)' }}>
                    {p.ownerName || p.ownerId || '-'}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                    {formatDate(p.createdAt)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: '1rem' }}>
                    <Link
                      to={`/projects/${p.id}/members`}
                      className="btn btn-ghost"
                      style={{
                        fontSize: '0.8rem',
                        marginRight: '0.4rem',
                        textDecoration: 'none',
                      }}
                    >
                      {t('projects.collaborators')}
                    </Link>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: '0.8rem', marginRight: '0.4rem' }}
                      onClick={() => openTransferModal(p)}
                    >
                      {t('projects.transfer')}
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: '0.8rem', color: 'var(--danger, #c0392b)' }}
                      onClick={() => openResetModal(p)}
                    >
                      {t('projects.reset')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
        }}
      >
        <span>{t('projects.total', { count: total })}</span>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.8rem' }}
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          {t('projects.prevPage')}
        </button>
        <span>
          {page} / {totalPages}
        </span>
        <button
          className="btn btn-ghost"
          style={{ fontSize: '0.8rem' }}
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages}
        >
          {t('projects.nextPage')}
        </button>
      </div>

      {transferTarget && (
        <TransferOwnerModal
          target={transferTarget}
          newOwnerId={newOwnerId}
          transferring={transferring}
          onSelectNewOwner={(user) => setNewOwnerId(user?.id ?? '')}
          onCancel={closeTransferModal}
          onConfirm={confirmTransfer}
        />
      )}

      {resetTarget && (
        <ResetProjectModal
          target={resetTarget}
          confirmText={resetConfirmText}
          resetting={resetting}
          onConfirmTextChange={setResetConfirmText}
          onCancel={closeResetModal}
          onConfirm={confirmReset}
        />
      )}
    </div>
  );
}
